import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.5 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = () => {
  const { t, dir } = useLang();
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yVideo = useTransform(scrollYProgress, [0, 1], ["0%", "15%"]);
  const scaleVideo = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  };

  // Fades the darkness toward the "end" side so the start side (where the
  // text sits) stays legible while the video is visible elsewhere.
  const sideGradient =
    dir === "rtl"
      ? "linear-gradient(to left, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.45) 35%, rgba(0,0,0,0.15) 65%, rgba(0,0,0,0) 95%)"
      : "linear-gradient(to right, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.45) 35%, rgba(0,0,0,0.15) 65%, rgba(0,0,0,0) 95%)";

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden flex items-center"
    >
      {/* FULL-WIDTH VIDEO BACKGROUND */}
      <motion.div
        style={{ y: yVideo, scale: scaleVideo }}
        className="absolute inset-0 z-0"
      >
        <video
          data-testid="hero-video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="w-full h-full object-cover"
        >
          <source src="/media/factory.webm" type="video/webm" />
          <source src="/media/factory.mp4" type="video/mp4" />
          <source
            src="https://customer-assets-jai6qajn.emergentagent.net/job_al-ezdhar-v2/artifacts/ffe6969f44f4746b_The%20factory%20For%20screens_123.mp4"
            type="video/mp4"
          />
        </video>
      </motion.div>

      {/* Directional side-shade so the start column (text) stays readable */}
      <div
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{ background: sideGradient }}
      />

      {/* TEXT COLUMN (anchored to inline-start — right in RTL, left in LTR) */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-28 pb-24"
      >
        <div className="w-full max-w-xl lg:max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#EBD9AC] mb-6"
          >
            {t.hero.kicker}
          </motion.p>

          <h1
            data-testid="hero-title"
            style={{ lineHeight: dir === "rtl" ? 1.15 : 0.98 }}
            className="font-display text-white text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] tracking-tightest mb-8"
          >
            {t.hero.title.map((line, i) => (
              <span className="mask-line" key={i}>
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  className="block"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="font-display italic text-2xl md:text-3xl text-[#EBD9AC] mb-5"
          >
            {t.hero.tagline}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="text-base md:text-lg text-white/90 leading-relaxed mb-10"
          >
            {t.hero.overview}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="flex flex-wrap gap-3"
          >
            <button
              data-testid="hero-cta-brands"
              onClick={() => scrollTo("brands")}
              className="bg-[#6A1E27] text-white px-8 py-4 hover:bg-[#4A151C] transition-colors"
            >
              {t.hero.cta1}
            </button>
            <button
              data-testid="hero-cta-b2b"
              onClick={() => scrollTo("b2b")}
              className="border border-white/70 text-white px-8 py-4 hover:bg-white hover:text-[#1A1A1A] transition-colors backdrop-blur-sm"
            >
              {t.hero.cta2}
            </button>
            <button
              data-testid="hero-cta-careers"
              onClick={() => navigate("/careers")}
              className="border border-white/70 text-white px-8 py-4 hover:bg-white hover:text-[#1A1A1A] transition-colors backdrop-blur-sm"
            >
              {t.hero.cta3}
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("about")}
        style={{ opacity }}
        className="absolute z-10 bottom-8 inset-x-0 mx-auto w-fit flex flex-col items-center gap-2 text-white/80 text-xs uppercase tracking-widest"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        {t.hero.scroll}
        <ArrowDown className="w-4 h-4" />
      </motion.button>
    </section>
  );
};

export default Hero;
